class CartPrivilegeProgress extends HTMLElement {
  connectedCallback() {
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.syncGifts = this.syncGifts.bind(this);

    document.addEventListener(
      'shopify:cart:lines-update',
      this.handleCartUpdate
    );

    this.scheduleGiftSync();
  }

  disconnectedCallback() {
    document.removeEventListener(
      'shopify:cart:lines-update',
      this.handleCartUpdate
    );

    window.clearTimeout(this.syncTimeout);
  }

  get giftsEnabled() {
    return this.dataset.giftsEnabled === 'true';
  }

  get giftVariants() {
    return {
      small: Number(this.dataset.smallCandleVariantId),
      large: Number(this.dataset.largeCandleVariantId),
    };
  }

  get thresholds() {
    return {
      small: Number(this.dataset.smallCandleThreshold),
      large: Number(this.dataset.largeCandleThreshold),
    };
  }

  handleCartUpdate(event) {
    event.promise
      ?.then(() => this.scheduleGiftSync())
      .catch(() => undefined);
  }

  scheduleGiftSync() {
    if (!this.giftsEnabled || this.syncing) return;

    window.clearTimeout(this.syncTimeout);
    this.syncTimeout = window.setTimeout(this.syncGifts, 0);
  }

  async syncGifts() {
    if (!this.giftsEnabled || this.syncing) return;

    this.syncing = true;

    try {
      const cart = await this.fetchCart();
      const qualifyingSubtotal = this.getQualifyingSubtotal(cart);
      const desiredGiftIds = new Set();
      const { small, large } = this.giftVariants;
      const { small: smallThreshold, large: largeThreshold } = this.thresholds;

      if (qualifyingSubtotal >= smallThreshold) desiredGiftIds.add(small);
      if (qualifyingSubtotal >= largeThreshold) desiredGiftIds.add(large);

      const giftLines = cart.items.filter(item =>
        item.variant_id === small || item.variant_id === large
      );

      const linesToRemove = giftLines.filter(item =>
        !desiredGiftIds.has(item.variant_id) ||
        giftLines.filter(line => line.variant_id === item.variant_id).indexOf(item) > 0
      );

      if (linesToRemove.length) {
        await this.updateCart({
          updates: Object.fromEntries(
            linesToRemove.map(item => [item.key, 0])
          ),
        });
        return;
      }

      const currentGiftIds = new Set(
        giftLines.map(item => item.variant_id)
      );

      const itemsToAdd = [...desiredGiftIds]
        .filter(variantId => !currentGiftIds.has(variantId))
        .map(variantId => ({
          id: variantId,
          quantity: 1,
          properties: {
            _shilpkala_privilege_gift: 'true',
          },
        }));

      if (itemsToAdd.length) {
        await this.addToCart({ items: itemsToAdd });
      }
    } catch (error) {
      console.warn(
        '[cart-privilege-progress] Gift synchronization failed.',
        error
      );
    } finally {
      this.syncing = false;
    }
  }

  getQualifyingSubtotal(cart) {
    const { small, large } = this.giftVariants;

    return cart.items.reduce((subtotal, item) => {
      if (item.variant_id === small || item.variant_id === large) {
        return subtotal;
      }

      return subtotal + item.original_line_price;
    }, 0);
  }

  async fetchCart() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Cart request failed: ${response.status}`);
    }

    return response.json();
  }

  async addToCart(body) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...body,
        sections: 'cart-drawer-section',
        sections_url: window.location.pathname,
      }),
    });

    await this.handleCartMutation(
      response,
      body.items.map(item => Number(item.id))
    );
  }

  async updateCart(body) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...body,
        sections: 'cart-drawer-section',
        sections_url: window.location.pathname,
      }),
    });

    await this.handleCartMutation(response);
  }

  async handleCartMutation(response, expectedGiftIds = []) {
    if (!response.ok) {
      throw new Error(`Cart mutation failed: ${response.status}`);
    }

    await response.json();

    /*
     * Shopify can acknowledge /cart/add.js before its automatic Buy X Get Y
     * allocation is reflected in the cart section HTML. Rendering that first
     * response leaves the drawer showing the old cart until a page refresh.
     *
     * Wait for the settled cart, then request fresh drawer HTML. These are
     * read-only requests: the successful add/update above remains the one and
     * only cart mutation.
     */
    const cart = await this.fetchSettledCart(expectedGiftIds);
    const sections = await this.fetchCartDrawerSection();
    const event = new CustomEvent('shopify:cart:lines-update', {
      bubbles: true,
    });

    event.action = 'update';
    event.promise = Promise.resolve({
      detail: {
        didError: false,
        items: cart.items,
        sections,
        source: 'cart-privilege-progress',
      },
    });

    document.dispatchEvent(event);
  }

  async fetchSettledCart(expectedGiftIds = []) {
    const maxAttempts = 5;
    let cart;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      cart = await this.fetchCart();

      if (
        this.giftDiscountsAreSettled(cart, expectedGiftIds) ||
        attempt === maxAttempts - 1
      ) {
        return cart;
      }

      await new Promise(resolve => window.setTimeout(resolve, 250));
    }

    return cart;
  }

  giftDiscountsAreSettled(cart, expectedGiftIds) {
    if (!expectedGiftIds.length) return true;

    return expectedGiftIds.every(variantId => {
      const item = cart.items.find(line =>
        Number(line.variant_id) === variantId &&
        line.properties?._shilpkala_privilege_gift === 'true'
      );

      return item &&
      item.final_line_price === 0 &&
      item.line_level_discount_allocations?.length > 0;
    });
  }

  async fetchCartDrawerSection() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', 'cart-drawer-section');

    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Cart drawer section request failed: ${response.status}`);
    }

    return response.json();
  }
}

if (!customElements.get('cart-privilege-progress')) {
  customElements.define(
    'cart-privilege-progress',
    CartPrivilegeProgress
  );
}
